import { AlertTriangle, ArrowLeft, ArrowRight, Save, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Button from "@/components/ui/Button";
import { ToastProvider, useToast } from "@/components/ui/Toast";
import { useRetailerSession } from "@/components/feature/retailer/RetailerSessionContext";
import { getSession } from "@/components/feature/auth/session";
import BankStep from "./components/BankStep";
import ChargesCard from "./components/ChargesCard";
import CustomerStep from "./components/CustomerStep";
import EmiStep from "./components/EmiStep";
import InsufficientBalance from "./components/InsufficientBalance";
import ProductStep from "./components/ProductStep";
import ReviewStep from "./components/ReviewStep";
import Stepper from "./components/Stepper";
import SuccessScreen from "./components/SuccessScreen";
import VerificationStep from "./components/VerificationStep";
import KycRequiredBlock from "@/pages/retailer/kyc/components/KycRequiredBlock";
import { emptyForm, getSteps, type FormErrors, type MandateForm } from "./types";
import { computeEmi, isDateToday } from "./emi";
import { adminService, mandateService, walletService } from "@/services/apiServices";
import { WALLET_RATES, setWalletRates } from "@/pages/retailer/wallet/constants";
import type { Mandate } from "@/types/mandate";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const IFSC_RE = /^[A-Z]{4}0[A-Z0-9]{6}$/;
const PAN_RE = /^[A-Z]{5}\d{4}[A-Z]$/;

const CURRENT_STEP_KEY: Record<string, number> = {
  "Customer Details": 1,
  "Product Details": 2,
  "EMI & Mandate": 3,
  "Bank Details": 4,
  Verification: 5,
  "Review & Submit": 6,
};

export default function RetailerCreateMandate() {
  return (
    <ToastProvider>
      <CreateMandateInner />
    </ToastProvider>
  );
}

function CreateMandateInner() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const draftId = searchParams.get("draftId");
  const toast = useToast();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState<MandateForm>(emptyForm);
  const [errors, setErrors] = useState<FormErrors>({});
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [loadingDraft, setLoadingDraft] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [success, setSuccess] = useState(false);
  const [createdMandate, setCreatedMandate] = useState<Mandate | null>(null);
  const [liveCreationRate, setLiveCreationRate] = useState<number | null>(null);
  const [liveInstallmentRate, setLiveInstallmentRate] = useState<number | null>(null);
  const {
    retailerId,
    wallet,
    isKycApproved,
    loading: sessionLoading,
    refreshWallet,
  } = useRetailerSession();

  useEffect(() => {
    if (!draftId) return;
    setLoadingDraft(true);
    mandateService
      .getById(draftId)
      .then((res) => {
        const m = res?.data?.mandate;
        if (m) {
          setForm({
            customerName: m.customerName || "",
            customerMobile: m.customerMobile || "",
            customerEmail: m.customerEmail || "",
            dob: m.customerDob ? m.customerDob.slice(0, 10) : "",
            customerPan: m.customerPan || "",
            aadhaar: m.customerAadhaar || "",
            address: m.customerAddress || "",
            city: m.customerCity || "",
            state: m.customerState || "",
            pincode: m.customerPincode || "",
            category: m.category || "",
            brand: m.brand || "",
            model: m.model || "",
            serialNo: m.serialNumber || "",
            invoiceNo: m.invoiceNumber || "",
            invoiceDate: m.invoiceDate ? m.invoiceDate.slice(0, 10) : "",
            description: m.productDescription || "",
            productPrice: m.productPrice != null ? String(m.productPrice) : "",
            downPayment: m.downPayment != null ? String(m.downPayment) : "0",
            interestRate: m.interestRate != null ? String(m.interestRate) : "0",
            tenure: m.tenureMonths != null ? String(m.tenureMonths) : "6",
            amount: m.emiAmount != null ? String(m.emiAmount) : "",
            authMode: m.authMode === "UPI Autopay" || m.authMode === "UPI_AUTO_PAY" ? "UPI_AUTO_PAY" : "ENACH",
            bankName: m.bankName || "",
            accountNumber: m.accountNumber || "",
            accountType: m.accountType || "Savings",
            ifsc: m.ifscCode || "",
            upiId: m.upiId || "",
            startDate: m.startDate ? m.startDate.slice(0, 10) : "",
            endDate: m.endDate ? m.endDate.slice(0, 10) : "",
            frequency: m.frequency || "Monthly",
            purpose: m.purpose || "EMI Collection",
            remarks: m.remarks || "",
            customerPhoto: m.customerPhoto || "",
            signature: m.signature || "",
            idProofType: m.idProofType || "",
            idProofFileName: m.idProofFileName || "",
            idProofUrl: m.idProofDocument || "",
            addressProofType: m.addressProofType || "",
            addressProofFileName: m.addressProofFileName || "",
            addressProofUrl: m.addressProofDocument || "",
            mpin: "",
          });
          toast.info(`Loaded draft mandate: ${m.refNo}`);
        }
      })
      .catch((err) => {
        toast.error(err?.message || "Failed to load draft details");
      })
      .finally(() => setLoadingDraft(false));
  }, [draftId]);

  useEffect(() => {
    refreshWallet();
    walletService
      .getRates()
      .then((res) => {
        if (res?.data) {
          const c = Number(res.data.creationRate ?? res.data.mandateCreationFee);
          const i = Number(res.data.installmentRate ?? res.data.installmentCharge ?? 40);
          const lb = Number(res.data.lowBalanceThreshold ?? 500);
          if (!isNaN(c) && c > 0) {
            setLiveCreationRate(c);
            setWalletRates(c, i, lb);
          }
          if (!isNaN(i)) setLiveInstallmentRate(i);
        }
      })
      .catch(() => {
        adminService
          .getSettings()
          .then((res) => {
            if (res?.data?.billingRates) {
              const b = res.data.billingRates;
              const c = Number(b.mandateCreationFee ?? b.creation);
              const i = Number(b.installmentCharge ?? b.installment ?? 40);
              const lb = Number(b.lowBalanceThreshold ?? 500);
              if (!isNaN(c) && c > 0) {
                setLiveCreationRate(c);
                setWalletRates(c, i, lb);
              }
              if (!isNaN(i)) setLiveInstallmentRate(i);
            }
          })
          .catch(() => undefined);
      });
  }, [refreshWallet]);

  const steps = getSteps(form.authMode);
  const totalSteps = steps.length;
  const currentLabel = steps[Math.min(step, totalSteps) - 1];
  const isReview = currentLabel === "Review & Submit";
  const stepKey = CURRENT_STEP_KEY[currentLabel] ?? 1;

  if (!sessionLoading && !isKycApproved) {
    return (
      <div className="mx-auto w-full max-w-[1200px] px-4 py-6 md:px-6">
        <KycRequiredBlock
          onBack={() => navigate("/retailer/dashboard")}
          onKyc={() => navigate("/retailer/kyc")}
        />
      </div>
    );
  }

  function setField(key: keyof MandateForm, value: string) {
    setForm((f) => ({ ...f, [key]: value }) as MandateForm);
    setErrors((e) => {
      const next = { ...e };
      delete next[key];
      if (key === "idProofFileName" || key === "idProofType") {
        delete next.idProofFileName;
        delete next.idProofType;
      }
      if (key === "addressProofFileName" || key === "addressProofType") {
        delete next.addressProofFileName;
        delete next.addressProofType;
      }
      return next;
    });
  }

  function setError(key: keyof MandateForm, message: string) {
    setErrors((e) => {
      const next = { ...e };
      if (message) next[key] = message;
      else delete next[key];
      return next;
    });
  }

  function parseDate(s: string): Date | null {
    const d = new Date(s);
    return isNaN(d.getTime()) ? null : d;
  }

  function todayStart(): Date {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return t;
  }

  function validate(stepToCheck: number): FormErrors {
    const errs: FormErrors = {};

    // Step 1: Customer Details
    if (stepToCheck === 1) {
      if (!form.customerName.trim()) {
        errs.customerName = "Customer name is required";
      }
      if (!form.customerMobile.trim()) {
        errs.customerMobile = "Customer mobile number is required";
      } else if (!/^\d{10}$/.test(form.customerMobile.trim())) {
        errs.customerMobile = "Enter a valid 10-digit mobile number";
      }
      if (!form.customerEmail.trim()) {
        errs.customerEmail = "Customer email address is required";
      } else if (!EMAIL_RE.test(form.customerEmail.trim())) {
        errs.customerEmail = "Enter a valid email address";
      }
      if (!form.dob) {
        errs.dob = "Select date of birth";
      } else {
        const d = parseDate(form.dob);
        if (!d || isNaN(d.getTime())) {
          errs.dob = "Enter a valid date of birth";
        } else if (d.getTime() > todayStart().getTime()) {
          errs.dob = "Date of birth cannot be in the future";
        } else {
          const today = new Date();
          let age = today.getFullYear() - d.getFullYear();
          const m = today.getMonth() - d.getMonth();
          if (m < 0 || (m === 0 && today.getDate() < d.getDate())) {
            age--;
          }
          if (age < 18) {
            errs.dob = `Customer must be at least 18 years old (current age: ${age} yr)`;
          }
        }
      }
      if (!form.customerPan.trim()) {
        errs.customerPan = "Customer PAN is required";
      } else if (!PAN_RE.test(form.customerPan.trim().toUpperCase())) {
        errs.customerPan = "Enter a valid 10-character PAN (e.g. ABCDE1234F)";
      }
      const rawAadhaar = form.aadhaar.replace(/\D/g, "");
      if (!rawAadhaar) {
        errs.aadhaar = "Aadhaar number is required";
      } else if (rawAadhaar.length !== 12) {
        errs.aadhaar = "Enter a valid 12-digit Aadhaar number";
      }
      if (!form.address.trim()) {
        errs.address = "Residential address is required";
      }
      if (!form.city.trim()) {
        errs.city = "City is required";
      }
      if (!form.state) {
        errs.state = "Select a state";
      }
      if (!form.pincode.trim()) {
        errs.pincode = "Pincode is required";
      } else if (!/^\d{6}$/.test(form.pincode.trim())) {
        errs.pincode = "Enter a valid 6-digit pincode";
      }
    }

    // Step 2: Product Details
    if (stepToCheck === 2) {
      if (!form.category) {
        errs.category = "Select a product category";
      }
      if (!form.brand.trim()) {
        errs.brand = "Brand is required";
      }
      if (!form.model.trim()) {
        errs.model = "Model is required";
      }
      if (!form.serialNo.trim()) {
        errs.serialNo = "Serial number or IMEI is required";
      }
      if (!form.invoiceNo.trim()) {
        errs.invoiceNo = "Invoice number is required";
      }
      if (!form.invoiceDate) {
        errs.invoiceDate = "Select the invoice date";
      } else {
        const d = parseDate(form.invoiceDate);
        if (d && d.getTime() > todayStart().getTime()) {
          errs.invoiceDate = "Invoice date cannot be in the future";
        }
      }
    }

    // Step 3: EMI & Mandate Details
    if (stepToCheck === 3) {
      const price = Number(form.productPrice);
      const down = Number(form.downPayment || 0);
      const rate = Number(form.interestRate);
      const isUpi = form.authMode === "UPI_AUTO_PAY" || form.authMode === "UPI Autopay";
      const startsToday = isDateToday(form.startDate);

      if (!form.productPrice || isNaN(price) || price <= 0) {
        errs.productPrice = "Enter a valid product price";
      }

      if (form.downPayment === "" || isNaN(down) || down < 0) {
        errs.downPayment = "Enter a valid down payment (0 if none)";
      } else if (price > 0 && down >= price) {
        errs.downPayment = "Down payment must be less than the product price.";
      } else if (!isUpi && down > 0) {
        // Rule 1: eNACH does not allow any advance collection/amount
        errs.downPayment = "eNACH does not allow any advance collection/amount. Please set down payment to ₹0 or select UPI Autopay.";
      } else if (isUpi && down > 15000) {
        // Rule 2: UPI cannot pay advance amount greater than 15000 (NPCI limit)
        errs.downPayment = "UPI AutoPay limit: Advance amount cannot exceed ₹15,000 (NPCI limit). Collect excess amount offline in-store or reduce down payment.";
      } else if (down > 0 && startsToday) {
        // Rule 3: Advance amount collection not allowed if mandate starts today
        errs.startDate = "Advance amount collection is not allowed if the mandate starts today. Please select a future start date or set down payment to ₹0.";
        if (!errs.downPayment) {
          errs.downPayment = "Advance amount collection is not allowed if the mandate starts today. Please set down payment to ₹0 or pick a future start date.";
        }
      }

      if (form.interestRate === "" || isNaN(rate) || rate < 0) {
        errs.interestRate = "Enter a valid interest rate";
      }

      const tenureNum = Number(form.tenure);
      if (!form.tenure || !form.tenure.trim() || isNaN(tenureNum) || tenureNum <= 0 || !Number.isInteger(tenureNum)) {
        errs.tenure = "Select or enter a valid tenure in months (e.g. 6)";
      } else if (tenureNum > 120) {
        errs.tenure = "Tenure cannot exceed 120 months (10 years)";
      }

      if (!form.authMode) {
        errs.authMode = "Select an authentication mode";
      }

      if (isUpi) {
        if (!form.upiId.trim()) {
          errs.upiId = "Enter the customer UPI ID";
        } else if (!/^[\w.-]+@[\w.-]+$/.test(form.upiId.trim())) {
          errs.upiId = "Enter a valid UPI ID (e.g. customer@okaxis)";
        }
      }

      const amount = Number(form.amount);
      if (!form.amount || isNaN(amount) || amount <= 0) {
        errs.amount = "Complete the EMI calculation above";
      }

      const max = Number(form.maxAmount);
      if (!form.maxAmount || isNaN(max) || max <= 0) {
        errs.maxAmount = "Enter a valid maximum amount";
      } else if (Number(form.amount) && max < Number(form.amount)) {
        errs.maxAmount = "Maximum amount must be equal to or greater than the mandate amount";
      }

      if (!form.startDate) {
        errs.startDate = "Select the first installment date";
      } else {
        const sd = parseDate(form.startDate);
        if (sd && sd.getTime() < todayStart().getTime()) {
          errs.startDate = "First installment date cannot be before today";
        }
      }

      if (!form.endDate) {
        errs.endDate = "End date is auto-calculated from the tenure";
      } else if (form.startDate) {
        const sd = parseDate(form.startDate);
        const ed = parseDate(form.endDate);
        if (sd && ed && ed.getTime() <= sd.getTime()) {
          errs.endDate = "End date must be after the first installment date";
        }
      }
    }

    // Step 4: Bank Details (only for eNACH)
    if (stepToCheck === 4) {
      const isUpi = form.authMode === "UPI_AUTO_PAY" || form.authMode === "UPI Autopay";
      if (!isUpi) {
        if (!form.bankName || !form.bankName.trim()) {
          errs.bankName = "Select or enter a bank name";
        } else if (form.bankName.trim().length < 2) {
          errs.bankName = "Enter a valid bank name (minimum 2 characters)";
        }
        if (!form.accountType) {
          errs.accountType = "Select an account type";
        }
        if (!form.accountNumber || !/^\d{6,18}$/.test(form.accountNumber.trim())) {
          errs.accountNumber = "Enter a valid account number (6 to 18 digits)";
        }
        if (!form.confirmAccountNumber) {
          errs.confirmAccountNumber = "Confirm your account number";
        } else if (form.confirmAccountNumber.trim() !== form.accountNumber.trim()) {
          errs.confirmAccountNumber = "Account numbers do not match";
        }
        if (!form.ifsc || !IFSC_RE.test(form.ifsc.trim().toUpperCase())) {
          errs.ifsc = "Enter a valid 11-character IFSC code (e.g. HDFC0001234)";
        }
      }
    }

    // Step 5: Customer Verification
    if (stepToCheck === 5) {
      if (!form.customerPhoto) {
        errs.customerPhoto = "Capture or upload the customer's photograph";
      }
      if (!form.idProofType) {
        errs.idProofType = "Select the ID proof document type";
      }
      if (!form.idProofFileName && !form.idProofUrl) {
        errs.idProofFileName = "Upload the ID proof document";
        if (!errs.idProofType) {
          errs.idProofType = "Upload the ID proof document";
        }
      }
      if (!form.addressProofType) {
        errs.addressProofType = "Select the address proof document type";
      }
      if (!form.addressProofFileName && !form.addressProofUrl) {
        errs.addressProofFileName = "Upload the address proof document";
        if (!errs.addressProofType) {
          errs.addressProofType = "Upload the address proof document";
        }
      }
      if (!form.signature) {
        errs.signature = "Customer signature is required";
      }
    }

    // Step 6: Review & Submit
    if (stepToCheck === 6) {
      const mpin = form.mpin.trim();
      if (!mpin) {
        errs.mpin = "Enter your 4-digit MPIN";
      } else if (!/^\d{4}$/.test(mpin)) {
        errs.mpin = "MPIN must be 4 digits";
      }
    }

    return errs;
  }

  function scrollToFirst(errs: FormErrors) {
    const firstKey = Object.keys(errs)[0];
    if (!firstKey) return;
    const el =
      document.getElementById(firstKey) ||
      document.getElementById(`${firstKey}-wrapper`) ||
      document.getElementById(`${firstKey}-select`);
    if (el) {
      el.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      if (typeof (el as any).focus === "function") {
        (el as any).focus();
      }
    }
  }

  function goToStep(next: number) {
    setStep(next);
    setErrors({});
  }

  function handleContinue() {
    const errs = validate(stepKey);
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      scrollToFirst(errs);
      const firstMsg = Object.values(errs)[0];
      if (firstMsg) toast.error(firstMsg);
      return;
    }
    setErrors({});
    if (step < totalSteps) {
      setStep(step + 1);
      return;
    }

    // Final pre-submit validation check across all mandatory steps
    const isUpi = form.authMode === "UPI_AUTO_PAY" || form.authMode === "UPI Autopay";
    const allStepKeys = isUpi ? [1, 2, 3, 5, 6] : [1, 2, 3, 4, 5, 6];

    for (const sKey of allStepKeys) {
      const stepErrs = validate(sKey);
      if (Object.keys(stepErrs).length > 0) {
        const targetLabel = Object.keys(CURRENT_STEP_KEY).find((k) => CURRENT_STEP_KEY[k] === sKey);
        const targetStepIndex = targetLabel ? steps.indexOf(targetLabel) + 1 : 1;
        if (targetStepIndex > 0 && targetStepIndex !== step) {
          setStep(targetStepIndex);
        }
        setErrors(stepErrs);
        scrollToFirst(stepErrs);
        const firstMsg = Object.values(stepErrs)[0];
        if (firstMsg) toast.error(firstMsg);
        return;
      }
    }
    setSubmitting(true);
    setSubmitError("");
    const session = getSession();
    mandateService
      .create({
        retailerId: session?.retailerId || retailerId,
        merchantToken: session?.merchantToken || (profile as any)?.merchant_token,
        deviceId: session?.deviceId,
        retailerMobile: session?.mobile,
        customerName: form.customerName,
        customerMobile: form.customerMobile,
        customerEmail: form.customerEmail,
        customerDob: form.dob,
        customerPan: form.customerPan,
        customerAadhaar: form.aadhaar,
        customerAddress: form.address,
        customerCity: form.city,
        customerState: form.state,
        customerPincode: form.pincode,
        category: form.category,
        brand: form.brand,
        model: form.model,
        serialNumber: form.serialNo,
        invoiceNumber: form.invoiceNo,
        invoiceDate: form.invoiceDate,
        productDescription: form.description,
        productPrice: form.productPrice,
        downPayment: form.downPayment,
        interestRate: form.interestRate,
        tenureMonths: form.tenure,
        authMode: form.authMode === "UPI_AUTO_PAY" ? "UPI Autopay" : "eNACH",
        bankName: form.bankName,
        accountNumber: form.accountNumber,
        accountType: form.accountType || "Savings",
        accountHolderName: form.customerName,
        ifscCode: form.ifsc,
        upiId: form.upiId,
        startDate: form.startDate,
        endDate: form.endDate,
        frequency: form.frequency || "Monthly",
        purpose: form.purpose || "EMI Collection",
        remarks: form.remarks,
        customerPhoto: form.customerPhoto,
        signature: form.signature,
        idProofType: form.idProofType,
        idProofFileName: form.idProofFileName,
        idProofDocument: form.idProofUrl,
        addressProofType: form.addressProofType,
        addressProofFileName: form.addressProofFileName,
        addressProofDocument: form.addressProofUrl,
        mpin: form.mpin,
      })
      .then((res) => {
        setSubmitting(false);
        const mandateData = res?.data?.mandate ?? null;
        const rocketPayData = res?.data?.rocketpay ?? null;
        if (mandateData && rocketPayData) {
          mandateData.rocketpay = rocketPayData;
          if (rocketPayData.checkoutUrl) mandateData.checkoutUrl = rocketPayData.checkoutUrl;
          if (rocketPayData.qrUrl) mandateData.qrUrl = rocketPayData.qrUrl;
          if (rocketPayData.id) mandateData.rocketpayMandateId = rocketPayData.id;
          if (rocketPayData.paymentOrderId) mandateData.paymentOrderId = rocketPayData.paymentOrderId;
        }
        setCreatedMandate(mandateData);
        setSuccess(true);
        refreshWallet();
      })
      .catch((err) => {
        setSubmitting(false);
        const errMsg = err?.message || "Failed to create mandate. Please try again.";
        setSubmitError(errMsg);
        toast.error(errMsg);
        window.scrollTo({ top: 0, behavior: "smooth" });
      });
  }

  async function handleSaveDraft() {
    if (!form.customerName?.trim() && !form.customerMobile?.trim()) {
      toast.error("Please enter at least customer name or mobile number before saving as draft");
      return;
    }
    setSavingDraft(true);
    try {
      const session = getSession();
      const payload = {
        retailerId: session?.retailerId || retailerId,
        merchantToken: session?.merchantToken || (session as any)?.merchant_token,
        deviceId: session?.deviceId,
        retailerMobile: session?.mobile,
        customerName: form.customerName,
        customerMobile: form.customerMobile,
        customerEmail: form.customerEmail,
        customerDob: form.dob,
        customerPan: form.customerPan,
        customerAadhaar: form.aadhaar,
        customerAddress: form.address,
        customerCity: form.city,
        customerState: form.state,
        customerPincode: form.pincode,
        category: form.category,
        brand: form.brand,
        model: form.model,
        serialNumber: form.serialNo,
        invoiceNumber: form.invoiceNo,
        invoiceDate: form.invoiceDate,
        productDescription: form.description,
        productPrice: form.productPrice,
        downPayment: form.downPayment,
        interestRate: form.interestRate,
        tenureMonths: form.tenure,
        authMode: form.authMode === "UPI_AUTO_PAY" ? "UPI Autopay" : "eNACH",
        bankName: form.bankName,
        accountNumber: form.accountNumber,
        accountType: form.accountType || "Savings",
        accountHolderName: form.customerName,
        ifscCode: form.ifsc,
        upiId: form.upiId,
        startDate: form.startDate,
        endDate: form.endDate,
        frequency: form.frequency || "Monthly",
        purpose: form.purpose || "EMI Collection",
        remarks: form.remarks,
        customerPhoto: form.customerPhoto,
        signature: form.signature,
        idProofType: form.idProofType,
        idProofFileName: form.idProofFileName,
        idProofDocument: form.idProofUrl,
        addressProofType: form.addressProofType,
        addressProofFileName: form.addressProofFileName,
        addressProofDocument: form.addressProofUrl,
        saveAsDraft: true,
      };

      if (draftId) {
        await mandateService.update(draftId, payload);
        toast.success("Draft mandate updated successfully");
      } else {
        await mandateService.create(payload);
        toast.success("Mandate saved as draft successfully");
      }
      navigate("/retailer/mandates?status=DRAFT");
    } catch (err: any) {
      toast.error(err?.message || "Failed to save draft");
    } finally {
      setSavingDraft(false);
    }
  }

  function handleReset() {
    setForm(emptyForm);
    setErrors({});
    setConfirmChecked(false);
    setSubmitting(false);
    setSubmitError("");
    setSuccess(false);
    setCreatedMandate(null);
    setStep(1);
  }

  const available = wallet?.availableBalance ?? 0;
  const creationCharge = liveCreationRate ?? wallet?.creationRate ?? WALLET_RATES.CREATION_CHARGE ?? 500;
  const installmentCharge = liveInstallmentRate ?? wallet?.installmentRate ?? WALLET_RATES.INSTALLMENT_CHARGE ?? 40;
  const blocked = !sessionLoading && available < creationCharge;
  const lowBalance = !blocked && available < (wallet?.lowBalanceThreshold ?? 500);
  const approxMandates = Math.max(0, Math.floor(available / creationCharge));
  const availableAfter = available - creationCharge;
  const calcEmi = computeEmi(form.productPrice, form.downPayment, form.interestRate, form.tenure);

  const primaryAuthLink =
    createdMandate?.checkoutUrl ||
    (createdMandate?.authLink?.startsWith("http")
      ? createdMandate.authLink
      : `${window.location.origin}${createdMandate?.authLink || `/auth/${createdMandate?.refNo}`}`);

  return (
    <div className="mx-auto w-full max-w-[1200px] px-1 py-3 sm:px-4 sm:py-6 md:px-6">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-ink">
            {draftId ? "Edit Draft Mandate" : "Create Mandate"}
          </h1>
          <p className="mt-1 text-sm text-ink-secondary">
            {draftId
              ? "Update draft details or generate authorisation link when ready"
              : "Fill in customer and mandate details to generate an authorisation link"}
          </p>
        </div>
        <Button
          variant="secondary"
          onClick={() => navigate("/retailer/mandates")}
          leftIcon={<X className="h-4 w-4" />}
        >
          Cancel
        </Button>
      </div>

      {blocked ? (
        <div className="mt-6 flex justify-center">
          <InsufficientBalance
            available={available}
            required={creationCharge}
            onBack={() => navigate("/retailer/mandates")}
            onTopup={() => navigate("/retailer/wallet?topup=1")}
          />
        </div>
      ) : (
        <>
          {lowBalance && (
            <div className="mt-6 flex flex-col items-start justify-between gap-3 rounded-[10px] border border-warning/40 bg-warning-bg px-4 py-3 sm:flex-row sm:items-center">
              <div className="flex items-start gap-3">
                <span className="mt-0.5 flex h-5 w-5 items-center justify-center text-warning">
                  <AlertTriangle className="h-5 w-5" />
                </span>
                <p className="text-sm text-ink">
                  Low balance — you can create approximately {approxMandates} more mandates.
                </p>
              </div>
              <Button variant="ghost" onClick={() => navigate("/retailer/wallet?topup=1")}>
                Top-up
              </Button>
            </div>
          )}

          {/* Stepper */}
          <div className="mt-6">
            <Stepper steps={steps} currentStep={step} onStepClick={goToStep} />
          </div>

          {/* Form card */}
          <div className="mt-6 overflow-hidden rounded-[10px] border border-line bg-card shadow-card">
            {success ? (
              <div className="flex justify-center p-6 md:p-10">
                <SuccessScreen
                  refNo={createdMandate?.refNo ?? ""}
                  status={createdMandate?.status || "CREATED"}
                  authLink={
                    createdMandate?.checkoutUrl ||
                    (createdMandate?.authLink?.startsWith("http")
                      ? createdMandate.authLink
                      : createdMandate
                        ? `${window.location.origin}${createdMandate.authLink}`
                        : "")
                  }
                  checkoutUrl={createdMandate?.checkoutUrl}
                  qrCode={createdMandate?.qrCode}
                  rocketpayMandateId={createdMandate?.rocketpayMandateId}
                  customerName={form.customerName}
                  customerMobile={form.customerMobile}
                  customerEmail={form.customerEmail}
                  brand={form.brand}
                  model={form.model}
                  serialNo={form.serialNo}
                  emi={createdMandate ? String(createdMandate.emiAmount) : form.amount}
                  tenure={form.tenure}
                  totalPayable={
                    createdMandate ? String(createdMandate.totalPayable) : calcEmi ? String(calcEmi.payable) : "0"
                  }
                  firstInstallment={form.startDate}
                  balanceAfter={availableAfter}
                  creationCharge={creationCharge}
                  rocketpayMandateId={createdMandate?.rocketpayMandateId || createdMandate?.rocketpay?.id}
                  checkoutUrl={createdMandate?.checkoutUrl || createdMandate?.rocketpay?.checkoutUrl}
                  qrUrl={createdMandate?.qrUrl || createdMandate?.rocketpay?.qrUrl}
                  paymentOrderId={createdMandate?.paymentOrderId || createdMandate?.rocketpay?.paymentOrderId}
                  onReset={handleReset}
                  onViewDetails={() => navigate(`/retailer/mandates/${createdMandate?.refNo ?? ""}`)}
                />
              </div>
            ) : (
              <>
                <div className="p-3.5 sm:p-6 md:p-8">
                  {currentLabel === "Customer Details" && (
                    <CustomerStep form={form} errors={errors ?? {}} setField={setField} />
                  )}
                  {currentLabel === "Product Details" && (
                    <ProductStep form={form} errors={errors ?? {}} setField={setField} />
                  )}
                  {currentLabel === "EMI & Mandate" && (
                    <EmiStep form={form} errors={errors ?? {}} setField={setField} />
                  )}
                  {currentLabel === "Bank Details" && (
                    <BankStep form={form} errors={errors ?? {}} setField={setField} setError={setError} />
                  )}
                  {currentLabel === "Verification" && (
                    <VerificationStep form={form} errors={errors ?? {}} setField={setField} />
                  )}
                  {currentLabel === "Review & Submit" && (
                    <ReviewStep
                      form={form}
                      onEdit={goToStep}
                      setField={setField}
                      confirmChecked={confirmChecked}
                      onConfirmChange={setConfirmChecked}
                      balanceAfter={availableAfter}
                      mpinError={errors?.mpin}
                      creationCharge={creationCharge}
                      installmentCharge={installmentCharge}
                    />
                  )}
                  {submitError && (
                    <div className="mt-4 flex items-start gap-3 rounded-lg border border-red-200 bg-danger-bg p-3.5">
                      <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-danger" />
                      <p className="text-[13px] leading-relaxed text-red-800">{submitError}</p>
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="sticky bottom-0 z-20 border-t border-line bg-card/95 px-4 py-3.5 backdrop-blur-md shadow-[0_-4px_12px_rgba(0,0,0,0.05)] sm:static sm:bg-card sm:px-6 sm:py-5 sm:shadow-none">
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-center">
                    {/* ChargesCard displayed on top of actions in Review step */}
                    {isReview && (
                      <div className="order-first lg:order-none lg:shrink-0">
                        <ChargesCard available={available} creationCharge={creationCharge} />
                      </div>
                    )}

                    <div className="flex flex-1 flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      {/* Left: Back button (Desktop) */}
                      <div className="hidden sm:block">
                        {step > 1 ? (
                          <Button
                            variant="ghost"
                            onClick={() => goToStep(step - 1)}
                            leftIcon={<ArrowLeft className="h-4 w-4" />}
                          >
                            Back
                          </Button>
                        ) : (
                          <span />
                        )}
                      </div>

                      {/* Right / Mobile Action Group */}
                      <div className="flex flex-col gap-2.5 sm:flex-row sm:items-center sm:gap-3">
                        {/* Mobile Back & Save as Draft row */}
                        <div className="flex items-center gap-2 sm:hidden">
                          {step > 1 && (
                            <Button
                              variant="ghost"
                              onClick={() => goToStep(step - 1)}
                              leftIcon={<ArrowLeft className="h-4 w-4" />}
                              className="flex-1 justify-center py-2 text-xs font-medium"
                            >
                              Back
                            </Button>
                          )}
                          <Button
                            variant="secondary"
                            leftIcon={<Save className="h-3.5 w-3.5" />}
                            onClick={handleSaveDraft}
                            loading={savingDraft}
                            disabled={submitting}
                            className="flex-1 justify-center py-2 text-xs font-medium"
                          >
                            {draftId ? "Update Draft" : "Save Draft"}
                          </Button>
                        </div>

                        {/* Desktop Save as Draft */}
                        <div className="hidden sm:block">
                          <Button
                            variant="secondary"
                            leftIcon={<Save className="h-4 w-4" />}
                            onClick={handleSaveDraft}
                            loading={savingDraft}
                            disabled={submitting}
                          >
                            {draftId ? "Update Draft" : "Save as Draft"}
                          </Button>
                        </div>

                        {/* Continue / Generate Mandate Link Button */}
                        <Button
                          onClick={handleContinue}
                          disabled={isReview && (!confirmChecked || form.mpin.length < 4)}
                          loading={submitting}
                          rightIcon={submitting ? undefined : <ArrowRight className="h-4 w-4" />}
                          className="w-full justify-center py-3 text-sm font-semibold shadow-sm sm:w-auto sm:py-2.5"
                        >
                          {submitting
                            ? "Generating..."
                            : isReview
                              ? "Generate Mandate Link"
                              : "Continue"}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}